// TODO: Review AI Code for feature: System Landscape & Interactions Visualization
import { useState, useRef, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranch, List, Workflow, Box } from 'lucide-react';
import type { SystemInteractions, SystemInteraction, SystemLogoData } from '@bliks-io/contracts';
import {
  cn,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  VisualizationFullscreenOverlay
} from '@bliks-io/ui';
import {
  ViewToggle,
  VisualizationToolbar,
  VisualizationFallback,
  type ViewModeOption
} from './VisualizationControls';
import { getLogoUrl } from './graph-3d-utils';

const SystemInteractionsFlow = lazy(() => import('./SystemInteractionsFlow'));
const SystemInteractions3D = lazy(() => import('./SystemInteractions3D'));

type ViewMode = 'list' | 'flow' | '3d';

export interface SystemInteractionsCardProps {
  currentSystem: { _id: string; name: string; logo?: SystemLogoData };
  interactions: SystemInteractions;
}

function SystemChip({ system }: { system: SystemInteraction }) {
  const logoUrl = getLogoUrl(system.logo);

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-md bg-background">
      {logoUrl && (
        <img src={logoUrl} alt={system.name} className="w-4 h-4 object-contain rounded-xs" />
      )}
      <span>{system.name}</span>
      <span className="text-muted-foreground">({system.count})</span>
    </div>
  );
}

function CurrentSystemChip({ name, logo }: { name: string; logo?: SystemLogoData }) {
  const logoUrl = getLogoUrl(logo);
  return (
    <div className="w-full px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold border rounded-md bg-muted/50">
      {logoUrl && <img src={logoUrl} alt={name} className="w-5 h-5 object-contain rounded-xs" />}
      {name}
    </div>
  );
}

function SystemInteractionsList({ currentSystem, interactions }: SystemInteractionsCardProps) {
  const { t } = useTranslation();
  const hasUpstream = interactions.upstream.length > 0;
  const hasDownstream = interactions.downstream.length > 0;

  return (
    <div className="relative pl-6">
      <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />

      <div className="relative pb-6">
        <div className="absolute -left-6 top-0.5 w-3.5 h-3.5 rounded-full border-2 border-primary bg-background" />
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t('organization.systems.interactions.upstream')}
          </span>
          {hasUpstream ? (
            <div className="flex flex-wrap gap-2">
              {interactions.upstream.map((system) => (
                <SystemChip key={system._id} system={system} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {t('organization.systems.interactions.noUpstream')}
            </p>
          )}
        </div>
      </div>

      <div className="relative py-4">
        <CurrentSystemChip name={currentSystem.name} logo={currentSystem.logo} />
      </div>

      <div className="relative pt-6">
        <div className="absolute -left-6 top-6 w-3.5 h-3.5 rounded-full border-2 border-primary bg-background" />
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t('organization.systems.interactions.downstream')}
          </span>
          {hasDownstream ? (
            <div className="flex flex-wrap gap-2">
              {interactions.downstream.map((system) => (
                <SystemChip key={system._id} system={system} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {t('organization.systems.interactions.noDownstream')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const VIEW_MODES: ViewModeOption<ViewMode>[] = [
  { value: 'list', icon: List },
  { value: 'flow', icon: Workflow },
  { value: '3d', icon: Box, hideOnMobile: true }
];

function VisualizationContent({
  viewMode,
  currentSystem,
  interactions,
  resetRef,
  isRotating,
  isFullscreen
}: {
  viewMode: ViewMode;
  currentSystem: SystemInteractionsCardProps['currentSystem'];
  interactions: SystemInteractionsCardProps['interactions'];
  resetRef: React.MutableRefObject<(() => void) | null>;
  isRotating: boolean;
  isFullscreen: boolean;
}) {
  return (
    <>
      {viewMode === 'list' && (
        <SystemInteractionsList currentSystem={currentSystem} interactions={interactions} />
      )}
      {viewMode === 'flow' && (
        <Suspense fallback={<VisualizationFallback />}>
          <SystemInteractionsFlow
            key={currentSystem._id}
            currentSystem={currentSystem}
            interactions={interactions}
            onResetRef={resetRef}
            isFullscreen={isFullscreen}
          />
        </Suspense>
      )}
      {viewMode === '3d' && (
        <Suspense fallback={<VisualizationFallback />}>
          <SystemInteractions3D
            key={currentSystem._id}
            currentSystem={currentSystem}
            interactions={interactions}
            onResetRef={resetRef}
            rotating={isRotating}
            isFullscreen={isFullscreen}
          />
        </Suspense>
      )}
    </>
  );
}

export function SystemInteractionsCard({
  currentSystem,
  interactions
}: SystemInteractionsCardProps) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRotating, setIsRotating] = useState(true);
  const resetRef = useRef<(() => void) | null>(null);

  const hasUpstream = interactions.upstream.length > 0;
  const hasDownstream = interactions.downstream.length > 0;

  const getLabel = useCallback(
    (mode: ViewMode) =>
      t(
        `organization.systems.interactions.view${mode === '3d' ? '3d' : mode.charAt(0).toUpperCase() + mode.slice(1)}`
      ),
    [t]
  );

  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

  const handleReset = useCallback(() => {
    resetRef.current?.();
  }, []);

  const handleToggleRotation = useCallback(() => setIsRotating((v) => !v), []);

  if (!hasUpstream && !hasDownstream) {
    return null;
  }

  const showControls = viewMode !== 'list';

  return (
    <>
      <Card className="h-full overflow-hidden">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              {t('organization.systems.interactions.title')}
            </CardTitle>
            <div className="flex w-full items-center justify-between gap-2">
              <div className={cn(!showControls && 'invisible')}>
                <VisualizationToolbar
                  show3DControls={viewMode === '3d'}
                  isFullscreen={false}
                  isRotating={isRotating}
                  onToggleRotation={handleToggleRotation}
                  onReset={handleReset}
                  onToggleFullscreen={toggleFullscreen}
                />
              </div>
              <ViewToggle
                modes={VIEW_MODES}
                active={viewMode}
                onChange={setViewMode}
                getLabel={getLabel}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!isFullscreen && (
            <VisualizationContent
              viewMode={viewMode}
              currentSystem={currentSystem}
              interactions={interactions}
              resetRef={resetRef}
              isRotating={isRotating}
              isFullscreen={false}
            />
          )}
        </CardContent>
      </Card>

      {isFullscreen && (
        <VisualizationFullscreenOverlay
          icon={GitBranch}
          title={t('organization.systems.interactions.title')}
          onClose={() => setIsFullscreen(false)}
          controls={
            <>
              {showControls && (
                <VisualizationToolbar
                  show3DControls={viewMode === '3d'}
                  isFullscreen
                  isRotating={isRotating}
                  onToggleRotation={handleToggleRotation}
                  onReset={handleReset}
                  onToggleFullscreen={toggleFullscreen}
                />
              )}
              <ViewToggle
                modes={VIEW_MODES}
                active={viewMode}
                onChange={setViewMode}
                getLabel={getLabel}
              />
            </>
          }
        >
          <VisualizationContent
            viewMode={viewMode}
            currentSystem={currentSystem}
            interactions={interactions}
            resetRef={resetRef}
            isRotating={isRotating}
            isFullscreen
          />
        </VisualizationFullscreenOverlay>
      )}
    </>
  );
}
